import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Calendar, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Employee } from "@/modules/hub/features/EmployeeDirectory/api/fetchEmployeeDirectoryProfiles/types";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";

interface EmployeeProfileCardProps {
  /** Highlights this tile as the viewer company's assigned coach (#9651). */
  isMyCoach?: boolean;
  member: Employee;
}

const memberDisplayName = (member: Employee): string => {
  const first = member.employee.firstName ?? "";
  const last = member.employee.lastName ?? "";
  const full = `${first} ${last}`.trim();
  return full.length > 0 ? full : member.email || member.uuid;
};

const memberInitials = (member: Employee): string => {
  const first = member.employee.firstName?.charAt(0) ?? "";
  const last = member.employee.lastName?.charAt(0) ?? "";
  const initials = `${first}${last}`.toUpperCase();
  return initials.length > 0 ? initials : "?";
};

const EmployeeProfileCard: React.FC<EmployeeProfileCardProps> = ({
  member,
  isMyCoach = false,
}) => {
  const name = memberDisplayName(member);
  const specialtiesText = member.specialties.map((s) => s.name).join(", ");

  return (
    <div
      className={
        isMyCoach
          ? "flex flex-col rounded-lg border-2 border-primary bg-white dark:bg-slate-800 overflow-hidden"
          : "flex flex-col rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
      }
    >
      <div className="aspect-square w-full bg-gray-100 dark:bg-slate-700 relative">
        {isMyCoach && (
          <span className="absolute top-3 left-3 z-10 rounded bg-primary px-2 py-0.5 text-xs font-medium text-white">
            Your Coach
          </span>
        )}
        {member.photo.publicUrl ? (
          <img
            alt={name}
            className="absolute inset-0 h-full w-full object-cover"
            src={member.photo.publicUrl}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-5xl font-light text-gray-400 dark:text-slate-500">
            {memberInitials(member)}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-center text-lg font-bold text-gray-900 dark:text-white">
          {name}
        </h3>
        <p className="mt-1 text-center text-sm font-semibold text-primary">
          {member.role}
        </p>
        {specialtiesText && (
          <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
            {specialtiesText}
          </p>
        )}

        <div className="my-4 border-t border-gray-200 dark:border-slate-700" />

        <div className="flex flex-1 flex-col gap-3">
          {member.primaryPhoneNumber && (
            <div className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-200">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
                <Phone className="h-3.5 w-3.5 text-primary" />
              </span>
              <span>{formatPhoneNumber(member.primaryPhoneNumber)}</span>
            </div>
          )}
          {member.email && (
            <div className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-200">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
                  <Mail className="h-3.5 w-3.5 text-primary" />
                </span>
                <a
                  className="truncate hover:underline"
                  href={`mailto:${member.email}`}
                >
                  {member.email}
                </a>
              </div>
              {member.linkedInUrl && (
                <a
                  aria-label={`${name} on LinkedIn`}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-[#0A66C2] text-white hover:opacity-90"
                  href={member.linkedInUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              )}
            </div>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          {member.bookCallUrl ? (
            <Button asChild className="flex-1 uppercase">
              <a
                href={member.bookCallUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book a Call
              </a>
            </Button>
          ) : (
            <Button className="flex-1 uppercase" disabled type="button">
              <Calendar className="mr-2 h-4 w-4" />
              Book a Call
            </Button>
          )}
          <Button asChild className="flex-1 uppercase" variant="outline">
            <Link to={`/employee-directory/${member.uuid}`}>View Bio</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfileCard;
